// Ayarlar > Organizasyon sekmesi — organizasyon teklifleri için varsayılanlar, hazır kalem / paket kütüphanesi
// ve PDF'in son sayfasındaki koşullar metni. Kendi kartlarını çizer; Settings.js yalnızca sekmeyi açar.
//
// Veri: settings kategorisi 'events' (eventApi.js → fetchEventSettings / saveEventSettings).
// Tek "Kaydet" tüm bölümleri birlikte kaydeder. Yazma izni (permissions.write) yoksa ekran salt okunurdur.
// Saklama biçimi (bkz. DEFAULT_EVENT_SETTINGS):
//   itemLibrary: [{ id, name, description, quantity, unit, unitPrice, vatRate }]  — id KORUNUR (teklif formu kalemi id ile seçer)
//   packages:    [{ id, name, items: [{ name, description, quantity, unit, unitPrice, vatRate }] }]
//   Kalemin KDV oranı kendi üzerinde (yemek %10, hizmet %20); oranı olmayan eski kayıtlar defaultVatRate'i alır.
//   termsText:   '' → örnek metin (DEFAULT_EVENT_TERMS). Metin örnekle aynıysa '' kaydedilir; böylece örnek metin
//                kodda güncellenirse, onu hiç düzenlememiş kurulumlara kendiliğinden yansır.
//   defaultVatMode / defaultVatRate / defaultValidityDays / defaultDepositPercent — yalnızca YENİ teklifin başlangıç değerleri.
// Kaydederken sunucudaki kayıtta bu ekranın bilmediği alanlar varsa korunur (üzerine yayılır).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  PartyPopper, SlidersHorizontal, Library, FileText, Save, Plus, Trash2, ChevronDown, ChevronUp, ArrowUp, ArrowDown,
  Copy, RotateCcw, AlertTriangle, AlertCircle, CheckCircle, Info, Lock, Undo2, Eye,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import { API_URL, jsonAuthHeaders } from '../../utils/api';
import { formatMoneyTR } from '../../utils/pricing';
import { VAT_MODES, toEventNumber, eventLineTotal, addDaysLocal, todayLocal } from '../../utils/eventPricing';
import {
  fetchEventSettings, saveEventSettings, DEFAULT_EVENT_SETTINGS, VAT_MODE_LABELS,
  newItemKey, itemToPayload, itemFromSaved, validateItems, emptyItem,
} from './eventApi';
import { parseTermsText, splitTitleLines, DEFAULT_EVENT_TERMS } from './eventTerms';
import EventItemsEditor from './EventItemsEditor';

const cardCls = 'bg-white dark:bg-gray-800 rounded-lg shadow p-6';
const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const inputCls = 'w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-70 disabled:cursor-not-allowed';
const badInputCls = 'border-red-400 dark:border-red-500';
const hintCls = 'text-xs text-gray-500 dark:text-gray-400 mt-1';
const errCls = 'text-xs text-red-600 dark:text-red-400 mt-1';
const codeCls = 'px-1.5 py-0.5 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 font-mono text-[11px] text-gray-800 dark:text-gray-100';

const EMPTY = [];

let pkgSeq = 0;
const newPackageId = () => `pkg-${Date.now().toString(36)}-${++pkgSeq}`;

// Kayıtlı sayı → Türkçe giriş metni (20 → "20", 12.5 → "12,5")
const fmtNum = (n) => {
  if (n === null || n === undefined || n === '') return '';
  const v = Number(n);
  return Number.isFinite(v) ? v.toLocaleString('tr-TR', { maximumFractionDigits: 2, useGrouping: false }) : '';
};
// "%20", "20", "12,5" → sayı; geçersiz → NaN (Number() KULLANILMAZ: "1.500" = 1,5 olurdu)
const percentNumber = (raw) => toEventNumber(String(raw ?? '').replace(/%/g, '').trim());

const normalizeTerms = (t) => String(t ?? '').replace(/\r\n/g, '\n').trim();
const isDefaultTerms = (t) => normalizeTerms(t) === normalizeTerms(DEFAULT_EVENT_TERMS);

// 'YYYY-MM-DD' → "14 Ekim 2026" (yerel kurulum, UTC kayması yok)
const fmtDateTR = (ymd) => {
  const [y, m, d] = String(ymd || '').split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
};
const fmtStamp = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Alan doğrulamaları → hata metni ya da ''
const vatRateError = (raw) => {
  const v = percentNumber(raw);
  return Number.isFinite(v) && v >= 0 && v <= 100 ? '' : 'KDV oranı 0 ile 100 arasında olmalı (ör. 20, 10, 1).';
};
// Ekrandaki varsayılan oran metni → kalem düzenleyicisinin yeni satır oranı (geçersizse %20)
const editorVatRate = (raw) => {
  const v = percentNumber(raw);
  return Number.isFinite(v) && v >= 0 && v <= 100 ? v : DEFAULT_EVENT_SETTINGS.defaultVatRate;
};
const validityError = (raw) => {
  const v = toEventNumber(raw);
  return Number.isInteger(v) && v >= 1 && v <= 365 ? '' : 'Geçerlilik süresi 1 ile 365 arasında tam gün olmalı.';
};
const depositError = (raw) => {
  const v = percentNumber(raw);
  return Number.isFinite(v) && v >= 0 && v <= 100 ? '' : 'Kapora yüzdesi 0 ile 100 arasında olmalı (ör. 30).';
};

// Kayıtlı id'yi anahtar olarak kullan; yoksa ya da tekrar ediyorsa yeni üret (React anahtarları çakışmasın)
const uniqueKey = (candidate, seen, make) => {
  const k = candidate !== undefined && candidate !== null && candidate !== '' ? String(candidate) : '';
  const key = k && !seen.has(k) ? k : make();
  seen.add(key);
  return key;
};

// Sunucu kaydı → ekran durumu (sayılar Türkçe metne çevrilir)
const formFromSettings = (s) => {
  const libSeen = new Set();
  const pkgSeen = new Set();
  const fallbackRate = s?.defaultVatRate ?? DEFAULT_EVENT_SETTINGS.defaultVatRate;
  const fromSaved = (e) => itemFromSaved(e, fallbackRate);
  return {
    vatMode: VAT_MODES.includes(s?.defaultVatMode) ? s.defaultVatMode : DEFAULT_EVENT_SETTINGS.defaultVatMode,
    vatRate: fmtNum(s?.defaultVatRate ?? DEFAULT_EVENT_SETTINGS.defaultVatRate),
    validityDays: fmtNum(s?.defaultValidityDays ?? DEFAULT_EVENT_SETTINGS.defaultValidityDays),
    depositPercent: fmtNum(s?.defaultDepositPercent ?? DEFAULT_EVENT_SETTINGS.defaultDepositPercent),
    library: (Array.isArray(s?.itemLibrary) ? s.itemLibrary : [])
      .filter(e => e && typeof e === 'object')
      .map(e => ({ ...fromSaved(e), key: uniqueKey(e.id, libSeen, newItemKey) })),
    packages: (Array.isArray(s?.packages) ? s.packages : [])
      .filter(p => p && typeof p === 'object')
      .map(p => ({
        id: uniqueKey(p.id, pkgSeen, newPackageId),
        name: String(p.name || ''),
        items: (Array.isArray(p.items) ? p.items : []).filter(it => it && typeof it === 'object').map(fromSaved),
      })),
    termsText: typeof s?.termsText === 'string' && s.termsText.trim() ? s.termsText : DEFAULT_EVENT_TERMS,
  };
};

// Ekran durumu → kayıt biçimi (sayılar)
const settingsFromForm = (f) => ({
  defaultVatMode: f.vatMode,
  defaultVatRate: percentNumber(f.vatRate),
  defaultValidityDays: toEventNumber(f.validityDays),
  defaultDepositPercent: percentNumber(f.depositPercent),
  itemLibrary: f.library.map(row => ({ id: row.key, ...itemToPayload(row) })),
  packages: f.packages.map(p => ({ id: p.id, name: p.name.trim(), items: p.items.map(itemToPayload) })),
  termsText: isDefaultTerms(f.termsText) ? '' : normalizeTerms(f.termsText),
});

// Kaydetmeden önce tüm bölümler → { message, packageId? } ya da null
const validateForm = (f) => {
  const fieldErr = vatRateError(f.vatRate) || validityError(f.validityDays) || depositError(f.depositPercent);
  if (fieldErr) return { message: `Varsayılanlar: ${fieldErr}` };
  if (f.library.length > 0) {
    const err = validateItems(f.library);
    if (err) return { message: `Hazır kalemler: ${err}` };
  }
  const names = new Set();
  for (let i = 0; i < f.packages.length; i++) {
    const p = f.packages[i];
    const name = p.name.trim();
    if (!name) return { message: `Paketler: ${i + 1}. paketin adını yazın.`, packageId: p.id };
    const k = name.toLocaleLowerCase('tr');
    if (names.has(k)) return { message: `Paketler: "${name}" adında birden fazla paket var; paket adları farklı olmalı.`, packageId: p.id };
    names.add(k);
    const err = validateItems(p.items);
    if (err) return { message: `"${name}" paketi: ${err}`, packageId: p.id };
  }
  return null;
};

// İşlem logu için bölüm adları
const SECTIONS = [
  ['Varsayılanlar', ['vatMode', 'vatRate', 'validityDays', 'depositPercent']],
  ['Hazır kalemler', ['library']],
  ['Paketler', ['packages']],
  ['Koşullar metni', ['termsText']],
];
const describeChanges = (before, after) => {
  const changed = SECTIONS
    .filter(([, keys]) => keys.some(k => JSON.stringify(before?.[k]) !== JSON.stringify(after[k])))
    .map(([label]) => label);
  const p = settingsFromForm(after);
  return `Güncellenen bölümler: ${changed.join(', ') || '—'}. `
    + `Hazır kalem: ${p.itemLibrary.length}, paket: ${p.packages.length}. `
    + `Varsayılanlar: ${VAT_MODE_LABELS[p.defaultVatMode]} %${fmtNum(p.defaultVatRate)}, `
    + `geçerlilik ${p.defaultValidityDays} gün, kapora %${fmtNum(p.defaultDepositPercent)}.`;
};

// Koşullar metninin PDF'e yakın önizlemesi (ardışık maddeler tek liste)
function TermsPreview({ parsed }) {
  const nodes = [];
  let bullets = [];
  const flushBullets = (key) => {
    if (!bullets.length) return;
    nodes.push(
      <ul key={`ul-${key}`} className="list-disc pl-5 space-y-1 mb-2">
        {bullets.map((t, i) => <li key={i}>{t}</li>)}
      </ul>
    );
    bullets = [];
  };
  parsed.blocks.forEach((b, i) => {
    if (b.type === 'bullet') { bullets.push(b.text); return; }
    flushBullets(i);
    nodes.push(b.type === 'heading'
      ? <h4 key={i} className="font-semibold text-gray-900 dark:text-gray-100 mt-3 mb-1 text-[13px]">{b.text}</h4>
      : <p key={i} className="mb-2">{b.text}</p>);
  });
  flushBullets('end');

  return (
    <div className="text-xs leading-relaxed text-gray-700 dark:text-gray-300">
      <div className="text-center font-bold text-sm text-gray-900 dark:text-white mb-3 tracking-wide">
        {splitTitleLines(parsed.title).map((line, i) => <div key={i}>{line}</div>)}
      </div>
      {nodes.length > 0 ? nodes : <p className="italic text-gray-400">Metin yok.</p>}
    </div>
  );
}

export default function EventSettings() {
  const { currentUser } = useAuth();
  const { incrementReads, incrementWrites } = useData();
  const canWrite = !!currentUser?.permissions?.write;
  const ro = !canWrite;

  const [form, setForm] = useState(null);           // ekrandaki (düzenlenen) hâl
  const [baseline, setBaseline] = useState(null);   // son yüklenen/kaydedilen hâl → "kaydedilmemiş değişiklik" karşılaştırması
  const [meta, setMeta] = useState(null);           // { updatedAt, updatedBy } — son kayıt bilgisi
  const [openPackages, setOpenPackages] = useState({});
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);       // { type: 'error' | 'success', text }
  const loadedRef = useRef(null);                   // sunucudaki son bilinen kayıt (eşzamanlı düzenleme kontrolü)
  const [loadError, setLoadError] = useState('');   // ayarlar okunamadı → form açılmaz (boş liste kayıtlıların üzerine yazılmasın)
  const [reloadKey, setReloadKey] = useState(0);
  const saveBarRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoadError('');
    fetchEventSettings().then((s) => {
      if (!alive) return;
      incrementReads(1);
      loadedRef.current = s;
      const f = formFromSettings(s);
      setForm(f);
      setBaseline(f);
      setMeta({ updatedAt: s?.updatedAt, updatedBy: s?.updatedBy });
    }, (err) => {
      if (alive) setLoadError(err?.message || 'Organizasyon ayarları alınamadı.');
    });
    return () => { alive = false; };
  }, [incrementReads, reloadKey]);

  const dirty = useMemo(
    () => !!form && !!baseline && JSON.stringify(form) !== JSON.stringify(baseline),
    [form, baseline]
  );

  // Kaydedilmemiş değişiklikle sekme/sayfa kapatılırken tarayıcı uyarısı
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const library = form ? form.library : EMPTY;
  // Paket düzenleyicisindeki "Hazır kalem ekle…" listesi (kayıt biçiminde; adı boş satırlar hariç)
  const librarySaved = useMemo(
    () => library.filter(r => String(r.name || '').trim()).map(r => ({ id: r.key, ...itemToPayload(r) })),
    [library]
  );
  const termsText = form ? form.termsText : '';
  const parsedTerms = useMemo(() => parseTermsText(termsText), [termsText]);

  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const updatePackage = (id, patch) => setForm(prev => ({
    ...prev, packages: prev.packages.map(p => (p.id === id ? { ...p, ...patch } : p)),
  }));
  const togglePackage = (id) => setOpenPackages(prev => ({ ...prev, [id]: !prev[id] }));

  const addPackage = () => {
    const id = newPackageId();
    setForm(prev => ({ ...prev, packages: [...prev.packages, { id, name: '', items: [emptyItem(editorVatRate(prev.vatRate))] }] }));
    setOpenPackages(prev => ({ ...prev, [id]: true }));
  };
  const duplicatePackage = (pkg) => {
    const id = newPackageId();
    const copy = {
      id,
      name: pkg.name.trim() ? `${pkg.name.trim()} (kopya)` : '',
      items: pkg.items.map(it => ({ ...it, key: newItemKey() })),
    };
    setForm(prev => {
      const i = prev.packages.findIndex(p => p.id === pkg.id);
      const next = [...prev.packages];
      next.splice(i + 1, 0, copy);
      return { ...prev, packages: next };
    });
    setOpenPackages(prev => ({ ...prev, [id]: true }));
  };
  const movePackage = (id, dir) => setForm(prev => {
    const i = prev.packages.findIndex(p => p.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= prev.packages.length) return prev;
    const next = [...prev.packages];
    [next[i], next[j]] = [next[j], next[i]];
    return { ...prev, packages: next };
  });
  const removePackage = (pkg) => {
    const label = pkg.name.trim() ? `"${pkg.name.trim()}" paketi` : 'Bu paket';
    if (!window.confirm(`${label} silinsin mi?\n\nSilme "Kaydet"e bastığınızda kalıcı olur. Daha önce bu paketle hazırlanmış teklifler etkilenmez.`)) return;
    setForm(prev => ({ ...prev, packages: prev.packages.filter(p => p.id !== pkg.id) }));
  };

  const restoreDefaultTerms = () => {
    if (!window.confirm('Koşullar metni örnek metinle değiştirilsin mi?\n\nBu metinde yaptığınız düzenlemeler kaybolur ("Kaydet"e basana kadar kalıcı değildir).')) return;
    setField('termsText', DEFAULT_EVENT_TERMS);
  };

  const revertChanges = () => {
    if (!window.confirm('Kaydedilmemiş tüm değişiklikler geri alınsın mı?')) return;
    setForm(baseline);
    setStatus(null);
  };

  const scrollToSave = () => saveBarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const handleSave = async () => {
    if (!canWrite || !form) return;
    if (!currentUser?.token) {
      setStatus({ type: 'error', text: 'Oturum bilgisi bulunamadı. Lütfen tekrar giriş yapın.' });
      return;
    }
    const problem = validateForm(form);
    if (problem) {
      setStatus({ type: 'error', text: problem.message });
      if (problem.packageId) setOpenPackages(prev => ({ ...prev, [problem.packageId]: true }));
      return;
    }

    setSaving(true);
    setStatus(null);
    try {
      // Bu ekran açıldıktan sonra başka biri kaydetmiş mi? (üzerine yazmadan önce sor)
      const latest = await fetchEventSettings();
      const known = loadedRef.current?.updatedAt ? String(loadedRef.current.updatedAt) : '';
      const current = latest?.updatedAt ? String(latest.updatedAt) : '';
      if (current && current !== known) {
        const who = latest.updatedBy?.email || 'başka bir kullanıcı';
        const when = fmtStamp(latest.updatedAt);
        const ok = window.confirm(
          `Organizasyon ayarları siz bu ekranı açtıktan sonra ${who} tarafından${when ? ` (${when})` : ''} değiştirilmiş.\n\n`
          + 'Kaydederseniz o değişikliklerin üzerine yazılır. Yine de kaydedilsin mi?'
        );
        if (!ok) {
          setStatus({ type: 'error', text: 'Kaydetme iptal edildi: ayarlar başka biri tarafından değiştirilmiş. Son hâli görmek için sayfayı yenileyin (kaydetmediğiniz değişiklikler kaybolur).' });
          return;
        }
      }

      const base = current ? latest : (loadedRef.current || {});
      const payload = {
        ...base,
        ...settingsFromForm(form),
        updatedBy: { uid: currentUser.uid, email: currentUser.email },
        updatedAt: new Date(),
      };
      await saveEventSettings(currentUser.token, payload);
      incrementWrites(2); // Ayar + Log

      try {
        await fetch(`${API_URL}/activityLogs`, {
          method: 'POST',
          headers: jsonAuthHeaders(currentUser.token),
          body: JSON.stringify({
            action: 'Organizasyon Ayarları Güncellendi',
            description: describeChanges(baseline, form),
            performedBy: { uid: currentUser.uid, email: currentUser.email },
          }),
        });
      } catch (logError) {
        console.warn('İşlem logu yazılamadı:', logError);
      }

      // Sunucuya giden hâli (updatedAt ISO metin) referans al; ekranı kayıt biçiminden yeniden kur
      const saved = JSON.parse(JSON.stringify(payload));
      loadedRef.current = saved;
      const next = formFromSettings(saved);
      setForm(next);
      setBaseline(next);
      setMeta({ updatedAt: saved.updatedAt, updatedBy: saved.updatedBy });
      setStatus({ type: 'success', text: 'Organizasyon ayarları kaydedildi.' });
    } catch (error) {
      console.error('Organizasyon ayarları kaydedilemedi:', error);
      setStatus({ type: 'error', text: error?.message || 'Organizasyon ayarları kaydedilemedi.' });
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <div className={cardCls}>
        <p className="text-sm text-red-600 dark:text-red-400 mb-3">
          {loadError} Kayıtlı paketlerin ve koşulların üzerine yanlışlıkla boş liste yazılmaması için ekran açılmadı.
        </p>
        <button type="button" onClick={() => setReloadKey(k => k + 1)}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700">Tekrar dene</button>
      </div>
    );
  }

  if (!form) {
    return (
      <div className={cardCls}>
        <LoadingSpinner />
      </div>
    );
  }

  const vatErr = vatRateError(form.vatRate);
  const daysErr = validityError(form.validityDays);
  const depErr = depositError(form.depositPercent);
  const days = toEventNumber(form.validityDays);
  const dep = percentNumber(form.depositPercent);

  const allOpen = form.packages.length > 0 && form.packages.every(p => openPackages[p.id]);
  const toggleAllPackages = () => setOpenPackages(allOpen ? {} : Object.fromEntries(form.packages.map(p => [p.id, true])));

  const termsBlank = !normalizeTerms(form.termsText);
  const termsIsDefault = isDefaultTerms(form.termsText);
  const termsTooLong = form.termsText.length > Math.round(DEFAULT_EVENT_TERMS.length * 1.25);
  const headingCount = parsedTerms.blocks.filter(b => b.type === 'heading').length;
  const bulletCount = parsedTerms.blocks.filter(b => b.type === 'bullet').length;

  const updatedStamp = fmtStamp(meta?.updatedAt);

  return (
    <div className="space-y-6">
      {/* ÜST BİLGİ */}
      <div className={cardCls}>
        <div className="flex items-center gap-3 mb-4">
          <PartyPopper className="text-pink-600" size={24} />
          <h2 className="text-lg font-semibold text-gray-700 dark:text-white">Organizasyon Teklifi Ayarları</h2>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm">
          Nişan, toplantı, mezuniyet gecesi gibi organizasyon tekliflerinin varsayılanlarını, teklif formunda tek tıkla
          eklenen hazır kalem ve paketleri, PDF'in son sayfasındaki koşullar metnini buradan yönetebilirsiniz.
          Tüm bölümler en alttaki <strong className="text-gray-700 dark:text-gray-200">Kaydet</strong> düğmesiyle birlikte kaydedilir.
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
          {updatedStamp
            ? <>Son kayıt: {updatedStamp}{meta?.updatedBy?.email ? ` · ${meta.updatedBy.email}` : ''}</>
            : 'Henüz kaydedilmiş bir ayar yok; varsayılan değerler gösteriliyor.'}
        </p>
        {ro && (
          <div className="mt-4 flex items-start gap-2 text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <Lock size={16} className="shrink-0 mt-0.5" />
            <span>Bu ayarları yalnızca görüntüleyebilirsiniz; değiştirmek için yazma yetkisi gerekir.</span>
          </div>
        )}
        {canWrite && dirty && (
          <button type="button" onClick={scrollToSave}
            className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-300 hover:underline">
            <AlertTriangle size={16} /> Kaydedilmemiş değişiklikler var — Kaydet düğmesine git
          </button>
        )}
      </div>

      <fieldset disabled={saving} className="min-w-0 space-y-6">
        {/* 1) VARSAYILANLAR */}
        <div className={cardCls}>
          <div className="flex items-center gap-3 mb-4">
            <SlidersHorizontal className="text-blue-600" size={24} />
            <h2 className="text-lg font-semibold text-gray-700 dark:text-white">Varsayılanlar</h2>
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-6 flex items-start gap-2">
            <Info size={16} className="shrink-0 mt-0.5" />
            <span>Bu değerler yalnızca yeni teklif açılırken forma gelir; her teklifte ayrıca değiştirilebilir. Kayıtlı teklifler etkilenmez.</span>
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <span id="eventVatModeLabel" className={labelCls}>Varsayılan KDV</span>
              <div role="radiogroup" aria-labelledby="eventVatModeLabel"
                className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                {VAT_MODES.map(m => (
                  <button key={m} type="button" role="radio" aria-checked={form.vatMode === m} disabled={ro}
                    onClick={() => setField('vatMode', m)}
                    className={`px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed ${form.vatMode === m
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'}`}>
                    {VAT_MODE_LABELS[m]}
                  </button>
                ))}
              </div>
              <p className={hintCls}>
                {form.vatMode === 'excluded'
                  ? 'Kalem fiyatları KDV hariç girilir; KDV genel toplamın üzerine eklenir.'
                  : 'Kalem fiyatları KDV dahil girilir; genel toplama ayrıca KDV eklenmez.'}
              </p>
            </div>

            <div>
              <label htmlFor="eventVatRate" className={labelCls}>Yeni kalemin KDV oranı (%)</label>
              <input id="eventVatRate" type="text" inputMode="decimal" autoComplete="off" disabled={ro}
                className={`${inputCls} md:max-w-[10rem] text-right ${vatErr ? badInputCls : ''}`}
                value={form.vatRate} onChange={e => setField('vatRate', e.target.value)} placeholder="20" />
              {vatErr
                ? <p className={errCls}>{vatErr}</p>
                : <p className={hintCls}>"Satır Ekle" ile açılan kalemin oranı. Oran her kalemde ayrıca seçilir (yemek %10, hizmet %20).</p>}
            </div>

            <div>
              <label htmlFor="eventValidityDays" className={labelCls}>Teklif geçerlilik süresi (gün)</label>
              <input id="eventValidityDays" type="text" inputMode="numeric" autoComplete="off" disabled={ro}
                className={`${inputCls} md:max-w-[10rem] text-right ${daysErr ? badInputCls : ''}`}
                value={form.validityDays} onChange={e => setField('validityDays', e.target.value)} placeholder="7" />
              {daysErr
                ? <p className={errCls}>{daysErr}</p>
                : <p className={hintCls}>Bugün açılan bir teklif {fmtDateTR(addDaysLocal(todayLocal(), days))} tarihine kadar geçerli olur.</p>}
            </div>

            <div>
              <label htmlFor="eventDepositPercent" className={labelCls}>Varsayılan kapora (%)</label>
              <input id="eventDepositPercent" type="text" inputMode="decimal" autoComplete="off" disabled={ro}
                className={`${inputCls} md:max-w-[10rem] text-right ${depErr ? badInputCls : ''}`}
                value={form.depositPercent} onChange={e => setField('depositPercent', e.target.value)} placeholder="30" />
              {depErr
                ? <p className={errCls}>{depErr}</p>
                : <p className={hintCls}>
                    {dep === 0
                      ? '0 = yeni tekliflerde kapora istenmez (teklifte ayrıca eklenebilir).'
                      : `Ör. 100.000 TL'lik teklifte ${formatMoneyTR(Math.round((100000 * dep) / 100))} TL kapora (genel toplam üzerinden).`}
                  </p>}
            </div>
          </div>
        </div>

        {/* 2) HAZIR KALEMLER + 3) PAKETLER */}
        <div className={cardCls}>
          <div className="flex items-center gap-3 mb-4">
            <Library className="text-green-600" size={24} />
            <h2 className="text-lg font-semibold text-gray-700 dark:text-white">Kalem ve Paket Kütüphanesi</h2>
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
            Sık kullanılan kalemleri (salon kirası, menü, pasta, DJ, süsleme…) bir kez tanımlayın; teklif formunda
            “Hazır kalem ekle…” ve “Paket ekle…” listelerinden tek tıkla eklenir. Teklife eklenen kalem o teklifte serbestçe
            düzenlenebilir; buradaki değişiklikler kayıtlı teklifleri etkilemez.
          </p>

          <EventItemsEditor title="Hazır Kalemler" items={form.library} onChange={rows => setField('library', rows)}
            currency="TL" readOnly={ro} defaultVatRate={editorVatRate(form.vatRate)} />

          <hr className="my-8 border-gray-200 dark:border-gray-700" />

          <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Paketler</h3>
            <div className="flex flex-wrap items-center gap-3">
              {form.packages.length > 1 && (
                <button type="button" onClick={toggleAllPackages}
                  className="text-sm text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white">
                  {allOpen ? 'Tümünü kapat' : 'Tümünü aç'}
                </button>
              )}
              {canWrite && (
                <button type="button" onClick={addPackage}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 flex items-center gap-1 text-sm font-medium">
                  <Plus size={16} /> Paket Ekle
                </button>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Paket, birlikte sunulan kalemlerin hazır listesidir (ör. “Nişan Paketi”: salon, menü, pasta, süsleme).
            Teklif formunda paket seçilince tüm kalemleri tek seferde eklenir. Paket adı ve en az bir kalem zorunludur.
          </p>

          {form.packages.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 p-4 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-center">
              Henüz paket yok.{canWrite ? ' "Paket Ekle" ile başlayın.' : ''}
            </p>
          )}

          <div className="space-y-3">
            {form.packages.map((pkg, index) => {
              const open = !!openPackages[pkg.id];
              const total = pkg.items.reduce((acc, it) => acc + eventLineTotal(it), 0);
              const displayName = pkg.name.trim() || 'Adsız paket';
              const bodyId = `event-package-${pkg.id}`;
              return (
                <div key={pkg.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800/60">
                    <button type="button" onClick={() => togglePackage(pkg.id)} aria-expanded={open} aria-controls={bodyId}
                      className="flex-1 min-w-0 flex items-center gap-2 text-left">
                      {open
                        ? <ChevronUp size={18} className="shrink-0 text-gray-500" />
                        : <ChevronDown size={18} className="shrink-0 text-gray-500" />}
                      <span className={`font-medium truncate ${pkg.name.trim() ? 'text-gray-800 dark:text-gray-100' : 'italic text-gray-400'}`}>
                        {displayName}
                      </span>
                      <span className="hidden sm:inline text-xs text-gray-500 dark:text-gray-400 shrink-0">
                        {pkg.items.length} kalem · {formatMoneyTR(total)} TL
                      </span>
                    </button>
                    {canWrite && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button type="button" title="Yukarı taşı" aria-label={`${displayName} paketini yukarı taşı`} disabled={index === 0}
                          onClick={() => movePackage(pkg.id, -1)}
                          className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 disabled:opacity-30">
                          <ArrowUp size={16} />
                        </button>
                        <button type="button" title="Aşağı taşı" aria-label={`${displayName} paketini aşağı taşı`} disabled={index === form.packages.length - 1}
                          onClick={() => movePackage(pkg.id, 1)}
                          className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 disabled:opacity-30">
                          <ArrowDown size={16} />
                        </button>
                        <button type="button" title="Paketi kopyala" aria-label={`${displayName} paketini kopyala`}
                          onClick={() => duplicatePackage(pkg)}
                          className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500">
                          <Copy size={16} />
                        </button>
                        <button type="button" title="Paketi sil" aria-label={`${displayName} paketini sil`}
                          onClick={() => removePackage(pkg)}
                          className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                  {open && (
                    <div id={bodyId} className="p-4 space-y-4 border-t border-gray-200 dark:border-gray-700">
                      <div>
                        <label htmlFor={`${bodyId}-name`} className={labelCls}>Paket adı *</label>
                        <input id={`${bodyId}-name`} type="text" maxLength={120} disabled={ro}
                          className={`${inputCls} ${!pkg.name.trim() ? badInputCls : ''}`}
                          value={pkg.name} onChange={e => updatePackage(pkg.id, { name: e.target.value })}
                          placeholder="ör. Nişan Paketi, Toplantı Paketi (Tam Gün)" />
                      </div>
                      <EventItemsEditor title="Paket kalemleri" items={pkg.items}
                        onChange={rows => updatePackage(pkg.id, { items: rows })}
                        currency="TL" library={librarySaved} readOnly={ro} defaultVatRate={editorVatRate(form.vatRate)} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* 4) ORGANİZASYON KOŞULLARI (PDF SON SAYFA) */}
        <div className={cardCls}>
          <div className="flex items-center gap-3 mb-4">
            <FileText className="text-purple-600" size={24} />
            <h2 className="text-lg font-semibold text-gray-700 dark:text-white">Organizasyon Koşulları (PDF son sayfa)</h2>
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
            Organizasyon teklifi PDF'inin son sayfasında yer alan rezervasyon, ödeme ve iptal koşulları. Değişiklik bundan
            sonra üretilen PDF'lere yansır; daha önce gönderilmiş PDF'ler değişmez.
          </p>

          <div className="mb-4 flex items-start gap-2 text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span>Örnek metin hukuki danışmanlık değildir; işletmenize göre düzenleyin.</span>
          </div>

          <div className="mb-4 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-xs text-gray-600 dark:text-gray-300">
            <p className="font-semibold text-gray-700 dark:text-gray-200 mb-2">Yazım kuralları</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div><code className={codeCls}># Başlık</code> sayfa başlığı (ilk satır)</div>
              <div><code className={codeCls}>## Bölüm</code> bölüm başlığı</div>
              <div><code className={codeCls}>- madde</code> madde işaretli satır</div>
              <div><span className="font-medium">Boş satır</span> paragrafları ayırır</div>
            </div>
            <p className="mt-2">
              Alt alta yazılan düz satırlar tek paragrafta birleşir; yeni paragraf için araya boş satır bırakın.
              Başlıkları PDF'te görünmesini istediğiniz gibi yazın (büyük harf için doğrudan büyük harfle: “İPTAL”, “ÖDEME”).
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="flex flex-col min-w-0">
              <label htmlFor="eventTermsText" className={labelCls}>Metin</label>
              <textarea id="eventTermsText" rows={22} readOnly={ro} spellCheck
                className={`${inputCls} text-sm leading-relaxed resize-y whitespace-pre-wrap min-h-[20rem] lg:h-[32rem]`}
                value={form.termsText} onChange={e => setField('termsText', e.target.value)}
                placeholder="# ORGANİZASYON REZERVASYON VE İPTAL KOŞULLARI" />
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {headingCount} bölüm · {bulletCount} madde · {form.termsText.length.toLocaleString('tr-TR')} karakter
                  {termsIsDefault ? ' · örnek metin' : ''}
                </span>
                {canWrite && (
                  <button type="button" onClick={restoreDefaultTerms} disabled={termsIsDefault}
                    className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                    <RotateCcw size={14} /> Örnek metni geri yükle
                  </button>
                )}
              </div>
              {termsBlank && (
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">Metin boş: PDF'te örnek metin kullanılacak (önizlemedeki gibi).</p>
              )}
              {termsTooLong && (
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                  Metin örnekten belirgin biçimde uzun; PDF'teki koşullar sayfası taşabilir. Kaydettikten sonra bir teklifin PDF'ini açıp kontrol edin.
                </p>
              )}
            </div>

            <div className="flex flex-col min-w-0">
              <span className={`${labelCls} flex items-center gap-1`}><Eye size={14} /> Önizleme</span>
              <div
                className="flex-1 border border-gray-200 dark:border-gray-700 rounded-md p-4 bg-white dark:bg-gray-900/40 overflow-y-auto max-h-[28rem] lg:max-h-none lg:h-[32rem]">
                <TermsPreview parsed={parsedTerms} />
              </div>
            </div>
          </div>
        </div>
      </fieldset>

      {/* KAYDET — tüm bölümler tek seferde */}
      {canWrite && (
        <div ref={saveBarRef} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1 min-w-0 text-sm" aria-live="polite">
            {status?.type === 'error' && (
              <p className="flex items-start gap-2 text-red-600 dark:text-red-400">
                <AlertCircle size={18} className="shrink-0 mt-0.5" /> <span>{status.text}</span>
              </p>
            )}
            {status?.type === 'success' && !dirty && (
              <p className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <CheckCircle size={18} className="shrink-0" /> <span>{status.text}</span>
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            {dirty && (
              <span className="text-sm font-medium text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <AlertTriangle size={16} className="shrink-0" /> Kaydedilmemiş değişiklikler var
              </span>
            )}
            {dirty && (
              <button type="button" onClick={revertChanges} disabled={saving}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm flex items-center gap-2 disabled:opacity-50">
                <Undo2 size={16} /> Geri al
              </button>
            )}
            <button type="button" onClick={handleSave} disabled={!dirty || saving}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
              <Save size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
